import {
  isBox,
  isCylinder,
  isExtrusion,
  isMeshInline,
  type SalonType,
  type WvmPart,
} from './types/wvm';

const num = (n: number): string => (Number.isInteger(n) ? String(n) : n.toFixed(1));

/** Kích thước W×H×D từ AABB (mm) của mảng đỉnh phẳng [x,y,z,...]. Trả null nếu rỗng. */
function extentsFromPositions(pos: number[]): [number, number, number] | null {
  if (pos.length < 3) return null;
  let mnx = Infinity,
    mny = Infinity,
    mnz = Infinity,
    mxx = -Infinity,
    mxy = -Infinity,
    mxz = -Infinity;
  for (let i = 0; i + 2 < pos.length; i += 3) {
    const x = pos[i],
      y = pos[i + 1],
      z = pos[i + 2];
    if (x < mnx) mnx = x;
    if (x > mxx) mxx = x;
    if (y < mny) mny = y;
    if (y > mxy) mxy = y;
    if (z < mnz) mnz = z;
    if (z > mxz) mxz = z;
  }
  return [mxx - mnx, mxy - mny, mxz - mnz];
}

/** Chuỗi kích thước hiển thị (viewer tự tính từ hình học — WVM không lưu). */
export function formatDims(part: WvmPart): string {
  const g = part.geometry;
  if (isBox(g)) return `${num(g.wx)}×${num(g.hy)}×${num(g.dz)} mm`;
  if (isCylinder(g)) return `Ø${num(g.radius * 2)}×${num(g.height)} mm`;
  // mesh nhúng (BAZIS): AABB world-space của đỉnh.
  if (isMeshInline(g)) {
    const e = extentsFromPositions(g.positions);
    if (e) return `${num(e[0])}×${num(e[1])}×${num(e[2])} mm`;
  }
  // panel uốn (extrusion): AABB mặt cắt 2D × chiều đùn (không gian đùn cục bộ).
  if (isExtrusion(g) && g.shape.length > 0) {
    let mnx = Infinity,
      mny = Infinity,
      mxx = -Infinity,
      mxy = -Infinity;
    for (const [x, y] of g.shape) {
      if (x < mnx) mnx = x;
      if (x > mxx) mxx = x;
      if (y < mny) mny = y;
      if (y > mxy) mxy = y;
    }
    return `${num(mxx - mnx)}×${num(mxy - mny)}×${num(g.height)} mm`;
  }
  return '';
}

/** Nhãn loại chuyển động (tiếng Việt) — nguồn: microcopy.vi.json. */
export const MOTION_LABELS: Record<SalonType, string> = {
  0: 'Cố định',
  2: 'Cửa bản lề phải',
  3: 'Cửa bản lề trái',
  4: 'Cửa lật xuống',
  5: 'Cửa nâng lên',
  6: 'Cửa trượt trái',
  7: 'Cửa trượt phải',
  8: 'Thân ngăn kéo',
  11: 'Mặt ngăn kéo',
  12: 'Aventos HS (nâng cung)',
  13: 'Piston (nâng thẳng)',
  14: 'Tay nâng Blum (gập đôi)',
  15: 'Trượt sâu (Z)',
};

export function motionLabel(part: WvmPart): string {
  const st = part.motion?.salonType ?? 0;
  return MOTION_LABELS[st] ?? 'Cố định';
}
